import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  Compass,
  Film,
  Wallet,
  Sparkles,
  AlertCircle,
  Eye,
  Award,
  ArrowRight,
  MapPin,
  Camera,
  Users,
  Trophy,
  UserCircle2,
} from "lucide-react";
import Logo from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";

const HERO_BG =
  "https://images.pexels.com/photos/2510650/pexels-photo-2510650.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

// Three-image collage that visualises the Cinevoya journey. These are
// placeholder stock images — go to /admin (Settings tab) to upload your
// own photographs of Black/African creators, audiences and festivals.
// Falls back to a branded gradient via onError if a URL ever breaks.
const VISION_FRAMES_DEFAULT = [
  {
    icon: Camera,
    eyebrow: "Frame 01",
    title: "The creator",
    body:
      "Filmmakers across the continent already make extraordinary work. Cinevoya gives that work a home.",
    image:
      "https://images.pexels.com/photos/3062541/pexels-photo-3062541.jpeg?auto=compress&cs=tinysrgb&w=1600",
    accent: "from-terracotta/40",
  },
  {
    icon: Users,
    eyebrow: "Frame 02",
    title: "The audience",
    body:
      "Viewers discover bold African shorts that ad-driven feeds bury — and pay creators directly for every story.",
    image:
      "https://images.pexels.com/photos/7991579/pexels-photo-7991579.jpeg?auto=compress&cs=tinysrgb&w=1600",
    accent: "from-gold/40",
  },
  {
    icon: Trophy,
    eyebrow: "Frame 03",
    title: "The destination",
    body:
      "AI matches each film to the festivals it qualifies for — turning unknown talent into recognised names.",
    image:
      "https://images.pexels.com/photos/3137890/pexels-photo-3137890.jpeg?auto=compress&cs=tinysrgb&w=1600",
    accent: "from-emerald-500/40",
  },
];

const PILLARS = [
  {
    icon: Film,
    eyebrow: "01 — Content destination",
    title: "A hand-curated library of premium African short films.",
    body:
      "Quality is the ticket to board. Our AI enforces standards before human review, so what makes it onto Cinevoya feels intentional, not algorithmic.",
    accent: "from-terracotta/25 to-transparent",
  },
  {
    icon: Wallet,
    eyebrow: "02 — Monetisation engine",
    title: "Creators earn on every journey.",
    body:
      "Pay-per-view today, subscriptions and licensing deals tomorrow. The platform earns only when creators earn — incentives stay aligned all the way through.",
    accent: "from-gold/20 to-transparent",
  },
  {
    icon: Award,
    eyebrow: "03 — Career platform",
    title: "Filmmaker profiles, spotlights and AI-matched festival submissions.",
    body:
      "The full journey, not just the first stop. A crew marketplace lands in Phase 3, turning each filmmaker page into a hub for collaborators and opportunities.",
    accent: "from-emerald-500/15 to-transparent",
  },
];

const PROBLEMS = [
  {
    n: "01",
    title: "Filmmakers create but cannot earn.",
    body:
      "Thousands of high-quality Nigerian short films are released for free on YouTube and Instagram each year because no structured monetisation channel exists specifically for this format.",
  },
  {
    n: "02",
    title: "Talent is invisible.",
    body:
      "Emerging directors, writers, and cinematographers have no professional discovery layer. Their work is buried in algorithmic feeds not built for film — and no platform connects them to opportunities.",
  },
  {
    n: "03",
    title: "Festival access is opaque and expensive.",
    body:
      "Routes into reputable festivals like FESPACO and AFRIFF are not accessible or affordable for most indie creators. There is no intelligent guide to help them navigate submissions.",
  },
];

export default function About() {
  // Fetch admin-uploaded image overrides. Any slot that's null falls back to
  // the built-in stock URL — so admins only need to upload what they want to
  // change, not all five slots.
  const [overrides, setOverrides] = useState({ hero: null, frame1: null, frame2: null, frame3: null, banner: null });
  // Admin-managed team roster; the section stays hidden until at least one
  // member has a photo, name and role.
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/site/about-images").then((r) => setOverrides(r.data || {})).catch(() => {});
    api.get("/site/team").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      // Only complete profiles (photo + name + role) are shown publicly.
      setTeam(list.filter((m) => m.photo_url && m.name?.trim() && m.role?.trim()));
    }).catch(() => {});
  }, []);

  const heroSrc = overrides.hero ? `${API}${overrides.hero}` : HERO_BG;
  const bannerSrc = overrides.banner
    ? `${API}${overrides.banner}`
    : "https://images.pexels.com/photos/7991471/pexels-photo-7991471.jpeg?auto=compress&cs=tinysrgb&w=1920";
  const visionFrames = VISION_FRAMES_DEFAULT.map((f, i) => {
    const slot = `frame${i + 1}`;
    return overrides[slot] ? { ...f, image: `${API}${overrides[slot]}` } : f;
  });

  return (
    <div className="min-h-screen bg-ink grain" data-testid="about-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-28 pb-20 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 -z-0">
          <img src={heroSrc} alt="" className="w-full h-full object-cover opacity-25" onError={(e) => { e.currentTarget.src = HERO_BG; }} />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/85 to-ink" />
        </div>
        <div className="relative px-6 sm:px-12 lg:px-20 max-w-[1400px] mx-auto">
          <Logo size={64} className="mb-8 drop-shadow-2xl" />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-terracotta/15 border border-terracotta/30 mb-6">
            <Compass className="w-3.5 h-3.5 text-gold" />
            <span className="text-xs uppercase tracking-widest text-sand font-medium">The Vision</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] max-w-4xl">
            The voyage from a bedroom in Lagos to a <span className="text-gradient-warm">screen at AFRIFF</span>.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-foreground/75 leading-relaxed max-w-3xl">
            Cinevoya is a curated, AI-powered short film platform that gives African filmmakers a
            structured home to monetise their work, grow their careers, and access the global film
            ecosystem — starting in Nigeria and scaling across the continent.
          </p>
        </div>
      </section>

      {/* ETYMOLOGY */}
      <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-[1400px] mx-auto" data-testid="about-etymology">
        <div className="rounded-2xl border border-white/10 bg-surface/60 p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-terracotta/10 blur-3xl" />
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-6">The name says everything</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative">
            <div className="border-l-2 border-terracotta pl-6 py-2">
              <p className="font-display text-3xl sm:text-4xl font-black mb-2">
                Cine<span className="text-foreground/30">·</span>
              </p>
              <p className="text-foreground/75 leading-relaxed">
                Film. The craft, the art form. The reason we gather in dark rooms and let stories rearrange us.
              </p>
            </div>
            <div className="border-l-2 border-gold pl-6 py-2">
              <p className="font-display text-3xl sm:text-4xl font-black mb-2">
                <span className="text-foreground/30">·</span>voya
              </p>
              <p className="text-foreground/75 leading-relaxed">
                The voyage. The journey, the vehicle. The platform that carries upcoming filmmakers from
                unknown to recognised, from unpaid to earning.
              </p>
            </div>
          </div>
          <p className="mt-10 text-foreground/80 leading-relaxed text-base sm:text-lg max-w-3xl relative">
            Every feature on Cinevoya exists to serve that journey. Curation protects the quality of what gets
            on board. Monetisation ensures creators earn along the way. The AI features act as the navigator
            — helping filmmakers find festivals, reach the right viewers, and connect with the right
            collaborators. The destination is a <span className="text-gold font-semibold">sustainable career in film</span>.
          </p>
        </div>
      </section>

      {/* VISION IN MOTION — three-frame visual story of the Cinevoya journey */}
      <section
        className="px-6 sm:px-12 lg:px-20 py-16 max-w-[1400px] mx-auto"
        data-testid="about-vision-in-motion"
      >
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-widest text-gold font-semibold mb-2 flex items-center gap-2">
            <Eye className="w-3 h-3" /> Vision in motion
          </p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight">
            The voyage in three frames.
          </h2>
          <p className="mt-3 text-foreground/65 text-sm sm:text-base max-w-2xl">
            From a director&apos;s viewfinder, to a packed cinema, to a festival podium — every feature on Cinevoya is built to carry filmmakers through this arc.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {visionFrames.map((f, i) => (
            <article
              key={f.eyebrow}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 bg-surface"
              data-testid={`vision-frame-${i}`}
            >
              <img
                src={f.image}
                alt={f.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              {/* Brand-tinted gradient overlay — keeps colour coherence across stock photography */}
              <div className={`absolute inset-0 bg-gradient-to-t ${f.accent} via-ink/55 to-ink/15`} />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
                <div className="w-9 h-9 rounded-full bg-ink/80 border border-white/15 backdrop-blur flex items-center justify-center mb-4">
                  <f.icon className="w-4 h-4 text-gold" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-foreground/55 mb-1.5">
                  {f.eyebrow}
                </p>
                <h3 className="font-display text-2xl font-black leading-tight mb-2">{f.title}</h3>
                <p className="text-sm text-foreground/85 leading-relaxed">{f.body}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-[1400px] mx-auto" data-testid="about-pillars">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2 flex items-center gap-2">
            <MapPin className="w-3 h-3" /> Three pillars of the journey
          </p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight">
            Content. Money. Career.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => (
            <div
              key={p.eyebrow}
              className={`relative p-7 rounded-xl border border-white/10 bg-surface overflow-hidden group hover:border-terracotta/40 transition-colors`}
              data-testid={`pillar-card-${i}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${p.accent} opacity-50 pointer-events-none`} />
              <div className="relative">
                <div className="w-11 h-11 rounded-lg bg-ink border border-white/10 flex items-center justify-center mb-5 group-hover:border-terracotta/40 transition-colors">
                  <p.icon className="w-5 h-5 text-terracotta" />
                </div>
                <p className="text-xs uppercase tracking-widest text-foreground/50 font-medium mb-2">
                  {p.eyebrow}
                </p>
                <h3 className="font-display text-xl font-bold leading-snug mb-3">{p.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PROBLEM WE SOLVE */}
      <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-[1400px] mx-auto" data-testid="about-problems">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2 flex items-center gap-2">
            <AlertCircle className="w-3 h-3" /> The problem we solve
          </p>
          <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight">
            Three pain points blocking the journey.
          </h2>
        </div>
        <div className="space-y-3">
          {PROBLEMS.map((p, i) => (
            <div
              key={p.n}
              className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-4 p-6 sm:p-8 rounded-xl border border-white/10 bg-surface/60 hover:bg-surface transition-colors"
              data-testid={`problem-card-${i}`}
            >
              <div>
                <p className="font-display text-5xl sm:text-6xl font-black text-terracotta/40 leading-none">
                  {p.n}
                </p>
              </div>
              <div>
                <h3 className="font-display text-xl sm:text-2xl font-bold mb-2">{p.title}</h3>
                <p className="text-foreground/75 leading-relaxed">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MEET THE TEAM — admin-managed roster of placeholder cards. Each card
          shows a photo (or initials fallback), name, role, and a short bio.
          Admins can add/edit/remove members from the admin dashboard → Site
          tab, so copy stays editable without a redeploy. */}
      {team.length > 0 && (
        <section className="px-6 sm:px-12 lg:px-20 py-16 max-w-[1400px] mx-auto" data-testid="about-team">
          <div className="max-w-3xl mb-12">
            <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2 flex items-center gap-2">
              <UserCircle2 className="w-3 h-3" /> Meet the team
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight">
              The people behind the voyage.
            </h2>
            <p className="mt-3 text-foreground/65 text-sm sm:text-base max-w-2xl">
              A small, dedicated team of curators, builders, and storytellers working to put African short cinema on the map.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {team.map((m) => {
              const photoSrc = m.photo_url ? `${API}${m.photo_url}` : null;
              const initials = (m.name || "")
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((w) => w[0]?.toUpperCase() || "")
                .join("") || "·";
              return (
                <article
                  key={m.id}
                  className="rounded-2xl border border-white/10 bg-surface overflow-hidden hover:border-terracotta/40 transition-colors flex flex-col"
                  data-testid={`team-card-${m.id}`}
                >
                  <div className="aspect-[4/5] relative bg-gradient-to-br from-terracotta/25 via-ink to-gold/15 overflow-hidden">
                    {photoSrc ? (
                      <img
                        src={photoSrc}
                        alt={m.name}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="font-display text-6xl font-black text-foreground/30 select-none">
                          {initials}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-display text-lg font-bold leading-tight" data-testid={`team-name-${m.id}`}>
                      {m.name}
                    </h3>
                    {m.role && (
                      <p className="text-xs uppercase tracking-widest text-terracotta font-semibold mt-1" data-testid={`team-role-${m.id}`}>
                        {m.role}
                      </p>
                    )}
                    {m.bio && (
                      <p className="text-sm text-foreground/70 leading-relaxed mt-3" data-testid={`team-bio-${m.id}`}>
                        {m.bio}
                      </p>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {/* DESTINATION BANNER — cinematic full-bleed image right before the CTA */}
      <section
        className="relative h-[280px] sm:h-[360px] my-8 overflow-hidden"
        data-testid="about-destination-banner"
      >
        <img
          src={bannerSrc}
          alt="African filmmakers at work on set"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        {/* Top + bottom fades so the image bleeds into the surrounding dark sections without hard edges */}
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-transparent to-ink" />
        {/* Brand-tinted overlay for colour cohesion */}
        <div className="absolute inset-0 bg-gradient-to-r from-terracotta/15 via-transparent to-gold/15" />
        <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
          <p className="font-display text-2xl sm:text-4xl font-black max-w-3xl leading-tight drop-shadow-[0_4px_24px_rgba(0,0,0,0.7)]">
            &ldquo;Every voyage begins with a single frame.&rdquo;
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 sm:px-12 lg:px-20 py-20 max-w-[1400px] mx-auto" data-testid="about-cta">
        <div className="rounded-2xl p-8 sm:p-16 bg-gradient-to-br from-terracotta/20 via-ember/10 to-transparent border border-terracotta/30 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-gold/15 blur-3xl" />
          <div className="relative max-w-2xl">
            <Sparkles className="w-6 h-6 text-gold mb-4" />
            <h2 className="font-display text-3xl sm:text-5xl font-bold mb-4 leading-tight">
              Board the voyage.
            </h2>
            <p className="text-foreground/75 mb-8 text-base sm:text-lg">
              Whether you make films or watch them, you belong here. Take the first step.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register">
                <Button className="bg-terracotta hover:bg-ember text-white h-12 px-7 text-base" data-testid="about-cta-register">
                  Start as a filmmaker <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/browse">
                <Button variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white h-12 px-7 text-base" data-testid="about-cta-browse">
                  <Eye className="w-4 h-4" /> Watch shorts
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter tagline="Curated African short cinema." showNav={false} maxWidth="max-w-[1400px]" />
    </div>
  );
}
